import { googleMapsUrl, navigateUrl, samePlace, type GeoPoint } from '@app/shared/geo';
import { formatPln } from '../catalog/money';
import { formatDecimal, parseDecimal } from '../ui/numbers';

/**
 * The place of an event as the forms edit it (owner remark 3/12); `routeKm`: "km wg trasy" as typed ('' = count
 * it), absent where the form does not edit it.
 */
export type Place = {
  location: string;
  locationAddress: string;
  locationPoint: GeoPoint | null;
  locationApprox: boolean;
  routeKm?: string | undefined;
  /** Spec 2026-10-22-mapy-google: the place in "Miejsca wydarzeń", its Google id, its notes (read only), the route. */
  venueId?: string | null | undefined;
  googlePlaceId?: string | undefined;
  placeNotes?: string | undefined;
  avoidTolls?: boolean | undefined;
  chosenRoute?: ChosenRoute | null | undefined;
  /**
   * Where the place of the ids is (the form's own memory, never sent): set by a pick, else the event's point when the
   * form opened. A pin moved further than ~300 m from it drops the ids (review I4).
   */
  placePoint?: GeoPoint | null | undefined;
};

/**
 * A route chosen in "Pokaż trasę": `routeId` (just picked) makes the server take its km, time and tolls from the
 * route it gave (review I2); time and tolls are missing for a role that may not see them.
 */
export type ChosenRoute = { routeId?: string; km: number; minutes?: number | null; tollsGrosze?: number | null; label: string };
/** One route of "Pokaż trasę" as the server sends it. */
export type RouteOption = ChosenRoute & { routeId: string; polyline: string; isDefault: boolean };

/** A change of the place from the field (never sets `routeKm` to undefined). */
export type PlacePatch = Partial<Omit<Place, 'routeKm'>> & { routeKm?: string };

export const APPROX_NOTE = 'Uwaga: lokalizacja nieprecyzyjna — sprawdź na miejscu albo zadzwoń.';

/** "Otwórz w Google Maps": the point, else the address, else the name of the place (its Google card with the id). */
export function placeMapsUrl(p: Place): string | null {
  return googleMapsUrl({ point: p.locationPoint, address: p.locationAddress.trim() || p.location.trim(), placeId: p.googlePlaceId || null });
}

/** "Nawiguj" (owner 2026-10-21 22:54): Google's navigation to the place only — the point, else the address — no waypoints. */
export function placeNavigateUrl(p: Place): string | null {
  return navigateUrl({ point: p.locationPoint, address: p.locationAddress.trim() || p.location.trim(), placeId: p.googlePlaceId || null, placePoint: p.placePoint ?? null });
}

/** Whether the place has ids of a place (Google or "Miejsca wydarzeń") that can go stale. */
const hasPlaceIds = (p: Place) => !!p.googlePlaceId || !!p.venueId;

/** The place ids dropped: they named another place (only the keys the form has, rule 7a). */
function placeIdsDropped(p: Place): PlacePatch {
  return { ...(p.googlePlaceId !== undefined ? { googlePlaceId: '' } : {}), ...(p.venueId !== undefined ? { venueId: null } : {}), placeNotes: '', placePoint: null };
}

/** The name or the address typed by hand: the place's ids go, the place is no longer the one picked (review I4). */
export function retypedPlacePatch(current: Place, typed: Pick<PlacePatch, 'location' | 'locationAddress'>): PlacePatch {
  return { ...typed, ...(hasPlaceIds(current) ? placeIdsDropped(current) : {}) };
}

/** A Google Maps link (long or short) typed or pasted into the place: the server reads the place from it. */
export function isMapsLink(text: string): boolean {
  return /^https:\/\/(maps\.app\.goo\.gl\/|goo\.gl\/maps\/|(www\.|maps\.)?google\.[a-z]{2,3}(\.[a-z]{2})?\/maps)/.test(text.trim());
}

/** A place picked from the suggestions, the company's places or a pasted link. */
export type PickedPlace = { name: string; address: string | null; point: GeoPoint | null; googlePlaceId: string; venueId: string | null; notes: string };

/**
 * The form's change after a pick: its name (when it has one), its address (when known), its point, its ids and notes;
 * a route chosen for the place before goes (spec §4.3).
 */
export function pickedPlacePatch(_current: Place, picked: PickedPlace): PlacePatch {
  return {
    ...(picked.name.trim() !== '' ? { location: picked.name.trim() } : {}),
    ...(picked.address ? { locationAddress: picked.address } : {}),
    ...(picked.point ? { locationPoint: picked.point } : {}),
    googlePlaceId: picked.googlePlaceId,
    venueId: picked.venueId,
    placeNotes: picked.notes,
    placePoint: picked.point,
    chosenRoute: null,
  };
}

/** "4 h 10 min", "45 min"; '' without a time. */
export function travelText(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h > 0 ? `${h} h` : '', m > 0 || h === 0 ? `${m} min` : ''].filter(Boolean).join(' ');
}

/** "opłaty drogowe ok. 45,50 zł", "bez opłat drogowych"; '' when unknown. */
export function tollsText(grosze: number | null | undefined): string {
  if (grosze === null || grosze === undefined) return '';
  return grosze === 0 ? 'bez opłat drogowych' : `opłaty drogowe ok. ${formatPln(grosze)}`;
}

/** One route of "Pokaż trasę": "A1 · 401,4 km · 4 h 20 min · opłaty drogowe ok. 45,50 zł". */
export function routeLine(r: ChosenRoute): string {
  return [r.label, `${formatDecimal(r.km)} km`, travelText(r.minutes), tollsText(r.tollsGrosze)].filter(Boolean).join(' · ');
}

/** "Kopiuj lokalizację": what goes into a message to the crew. */
export function placeCopyText(p: Place & { name: string }): string {
  const title = [p.name.trim(), p.location.trim()].filter(Boolean).join(' — ');
  const url = placeMapsUrl(p);
  if (!title && !p.locationAddress.trim() && !url) return '';
  return [title, p.locationAddress.trim(), url ?? '', p.locationApprox ? APPROX_NOTE : ''].filter(Boolean).join('\n');
}

export function distanceLabel(km: number | null | undefined): string {
  if (km === null || km === undefined) return '';
  return km === 0 ? 'przy magazynie' : `ok. ${km} km od magazynu`;
}

/** A found address often starts with the name of the place ("Filharmonia Bałtycka, Ołowianka 1, …"): say it once. */
export function addressWithoutName(address: string, name: string): string {
  const prefix = `${name.trim()}, `;
  return name.trim() !== '' && address.toLocaleLowerCase('pl-PL').startsWith(prefix.toLocaleLowerCase('pl-PL')) ? address.slice(prefix.length) : address;
}


/**
 * What the big map hands back: the pin, whether it was put by hand, and what the server found there; a place picked in
 * its search also its ids (a pin moved by hand keeps the place's ids: the entrance of the same hall).
 */
export type MapChoice = { point: GeoPoint; exact: boolean; address: string | null; name: string; picked?: Pick<PickedPlace, 'googlePlaceId' | 'venueId' | 'notes'> | undefined };

/**
 * The form's change after "Użyj tego miejsca": the point; exact when the pin was put by hand; the found address
 * replaces the field (the dialog shows it before the choice); the found name fills only an empty "Miejsce".
 */
export function mapChoicePatch(current: Place, choice: MapChoice): PlacePatch {
  const patch: PlacePatch = { locationPoint: choice.point };
  if (choice.exact) patch.locationApprox = false;
  if (choice.address) patch.locationAddress = choice.address;
  if (current.location.trim() === '' && choice.name.trim() !== '') patch.location = choice.name.trim();
  if (choice.picked) Object.assign(patch, { googlePlaceId: choice.picked.googlePlaceId, venueId: choice.picked.venueId, placeNotes: choice.picked.notes, placePoint: choice.point });
  else if (hasPlaceIds(current)) {
    // the ids stay at the place they name: measured from it, not from the last pin (review I4)
    const at = current.placePoint ?? current.locationPoint;
    if (at && samePlace(at, choice.point)) patch.placePoint = at;
    else Object.assign(patch, placeIdsDropped(current));
  }
  // a route chosen for the old point goes (the server says the same)
  if (current.chosenRoute && (!current.locationPoint || current.locationPoint.lat !== choice.point.lat || current.locationPoint.lng !== choice.point.lng)) patch.chosenRoute = null;
  return patch;
}

/** The place fields of spec 2026-10-22-mapy-google a form sends, by field visibility (rule 7a). */
const GOOGLE_FIELDS = ['venueId', 'googlePlaceId', 'avoidTolls', 'chosenRoute'] as const;

/** The form's place without what the role may not see (the field hides its controls for an `undefined`). */
export function visiblePlace<T extends Place>(p: T, visible: (field: string) => boolean): T {
  const out = { ...p };
  for (const f of GOOGLE_FIELDS) if (!visible(f)) delete out[f];
  if (!visible('placeNotes')) delete out.placeNotes;
  return out;
}

/** The chosen route as the server takes it: without the tolls for whoever may not see them. */
const routeToSend = (r: ChosenRoute | null, visible: (field: string) => boolean): ChosenRoute | null =>
  r
    ? {
        ...(r.routeId ? { routeId: r.routeId } : {}),
        km: r.km,
        ...(r.minutes !== undefined ? { minutes: r.minutes } : {}),
        label: r.label,
        ...(visible('tollsGrosze') && (r.tollsGrosze !== undefined || !r.routeId) ? { tollsGrosze: r.tollsGrosze ?? null } : {}),
      }
    : null;

/** Adding an event: the place's ids, "Unikaj płatnych" and the chosen route when set and visible. */
export function placeCreateFields(p: Place, visible: (field: string) => boolean = () => true): Record<string, unknown> {
  return {
    ...(p.venueId && visible('venueId') ? { venueId: p.venueId } : {}),
    ...(p.googlePlaceId && visible('googlePlaceId') ? { googlePlaceId: p.googlePlaceId } : {}),
    ...(p.avoidTolls && visible('avoidTolls') ? { avoidTolls: true } : {}),
    ...(p.chosenRoute && visible('chosenRoute') ? { chosenRoute: routeToSend(p.chosenRoute, visible) } : {}),
  };
}

/** Editing: only what changed and is visible (rule 7a). */
export function placeEditFields(before: Place, after: Place, visible: (field: string) => boolean): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const differs = (k: (typeof GOOGLE_FIELDS)[number]) => JSON.stringify(before[k] ?? null) !== JSON.stringify(after[k] ?? null);
  if (visible('venueId') && differs('venueId')) out.venueId = after.venueId ?? null;
  if (visible('googlePlaceId') && differs('googlePlaceId')) out.googlePlaceId = after.googlePlaceId ?? '';
  if (visible('avoidTolls') && differs('avoidTolls')) out.avoidTolls = after.avoidTolls ?? false;
  if (visible('chosenRoute') && differs('chosenRoute')) out.chosenRoute = routeToSend(after.chosenRoute ?? null, visible);
  return out;
}

export type DistanceSource = 'chosen' | 'route' | 'google';
export type Distance = { routeKm: number | null; distanceKm: number | null; distanceSource: DistanceSource | null; chosenLabel?: string | undefined };

/**
 * The distance of an event and where it comes from (owner: "a jak liczysz odległość?"); '' without one — no km are
 * guessed (owner 2026-10-23: no straight line × road factor).
 */
export function distanceText(d: Distance): string {
  if (d.distanceSource === 'chosen' && d.distanceKm !== null) return `${d.distanceKm} km od magazynu wg wybranej trasy${d.chosenLabel ? ` (${d.chosenLabel})` : ''}`;
  if (d.distanceSource === 'route' && d.routeKm !== null) return `${formatDecimal(d.routeKm)} km od magazynu wg trasy (wpisane)`;
  if (d.distanceKm === null || d.distanceKm === undefined) return '';
  // typed, but the role does not see "km wg trasy": the rounded distance, still said where it comes from
  if (d.distanceSource === 'route') return `${d.distanceKm} km od magazynu wg trasy (wpisane)`;
  if (d.distanceKm === 0) return distanceLabel(0);
  if (d.distanceSource === 'google') return `${distanceLabel(d.distanceKm)} wg trasy Google`;
  return distanceLabel(d.distanceKm);
}

const ROUTE_KM_MAX = 20000;

/** "km wg trasy" as typed: a number with at most one decimal (comma or dot), '' = count the distance. */
export function parseRouteKm(text: string): { ok: true; value: number | null } | { ok: false } {
  if (text.trim() === '') return { ok: true, value: null };
  const n = parseDecimal(text, { maxFractionDigits: 1 });
  return n !== null && n <= ROUTE_KM_MAX ? { ok: true, value: n } : { ok: false };
}

export const routeKmText = (km: number | null | undefined): string => (km === null || km === undefined ? '' : formatDecimal(km));

/**
 * "km wg trasy" as the form shows it (owner 2026-10-23): the km that count and where they come from — typed by hand,
 * else the chosen route, else the automatic Google route; without any of them no km are guessed (owner 2026-10-23:
 * "brak trasy Google — wpisz km"). The km from the route are a placeholder (review M5), never the input's value: only typed km are a value (and sent, rule 7a). `restore`:
 * what "przywróć z trasy" goes back to (only for typed km).
 */
export type RouteKmField = { value: string; suggested: string; typed: boolean; note: string; restore: string | null };

export function routeKmField(input: {
  typed: string;
  chosen: Pick<ChosenRoute, 'km' | 'label'> | null;
  /** The saved automatic (Google) distance of this very point and "Unikaj płatnych" (null: none). */
  auto: { km: number; source: 'google' } | null;
  /** The warehouse is set (without it there is nothing to count from: the note above the field says so). */
  warehouse: boolean;
  /** The point or "Unikaj płatnych" in the form differ from the saved ones (the Google route comes on saving). */
  pending: boolean;
  /** The server counts Google routes. */
  routesOn: boolean;
  /** The default route of the new point is being asked (owner 2026-10-23 13:34). */
  checking?: boolean | undefined;
}): RouteKmField {
  const route: { suggested: string; note: string } | null = input.chosen
    ? { suggested: formatDecimal(input.chosen.km), note: `trasa wybrana${input.chosen.label ? ` (${input.chosen.label})` : ''}` }
    : input.auto
      ? { suggested: String(input.auto.km), note: 'trasa Google (domyślna)' }
      : null;
  if (input.typed.trim() !== '') return { value: input.typed, suggested: route?.suggested ?? '', typed: true, note: 'wpisane ręcznie', restore: route?.note ?? null };
  const counted = route ?? { suggested: '', note: !input.warehouse ? '' : input.routesOn && input.pending ? (input.checking ? 'liczę trasę Google…' : 'trasa Google po zapisaniu') : 'brak trasy Google — wpisz km' };
  return { value: '', ...counted, typed: false, restore: null };
}
